/**
 * Field Evidence View
 *
 * Renders the transcript/notes evidence for one extracted field — the header
 * (Question / Answer + badges), an Overview/Full Text tab group, and the
 * transcript + notes evidence columns. Extracted from EvidenceModal so both the
 * evidence popup and the validation popup can show the same evidence. Fills its
 * container; the caller supplies the surrounding Modal/pane sizing.
 */

import React, { useState, useMemo } from 'react';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import Text from '@amzn/meridian/text';
import Toggle from '@amzn/meridian/toggle';
import Button from '@amzn/meridian/button';
import { useAppSelector } from '../../../store/store';
import { selectMergedSegments } from '../../../store/transcriptSlice';
import {
  FieldValue,
  fieldValuesEqual,
  getFieldLabel,
  getFieldQuestion,
  getMeaningfulGroupEntries,
  isFieldPending,
  isGroupedFieldValue,
} from '../../../utils/fieldUtils';
import { formatISOTimestampToDateTime } from '../../../utils/dateUtil';
import EvidenceHighlightedText from './EvidenceHighlightedText';
import {
  FONT_SIZE_BODY,
  FONT_SIZE_SMALL,
  LINE_HEIGHT_BODY,
  TEXT_COLOR_PRIMARY,
  TEXT_COLOR_SECONDARY,
  TEXT_COLOR_LINK,
  EVIDENCE_HIGHLIGHT_BG,
  EVIDENCE_HIGHLIGHT_BORDER,
  EVIDENCE_HIGHLIGHT_TEXT,
} from '../cssConstants';

const CONTEXT_CHARS = 80; // chars of context around evidence in overview

interface FieldEvidenceViewProps {
  // One or more related fields (a dependency group) shown together. First is primary.
  fieldNames: string[];
  // When provided, an "Edit" link is shown by the answer (e.g. the evidence modal wires
  // this to open an inline editor panel). Omitted in read-only contexts.
  onEdit?: () => void;
  /** Greys Edit out instead of hiding it, for viewers who may read but not change the value. */
  editDisabled?: boolean;
}

type ViewMode = 'overview' | 'fulltext';

/** Human-readable one-line rendering of a field value (grouped / list / scalar). */
function formatValue(v: FieldValue | undefined): string {
  if (v === undefined || v === null) return '—';
  if (isGroupedFieldValue(v)) {
    const parts = getMeaningfulGroupEntries(v).map(([k, vals]) => {
      const arr = Array.isArray(vals) ? vals : [vals];
      return `${k}: ${arr.filter((x) => x !== '<UNKNOWN>').join(', ')}`;
    });
    return parts.length ? parts.join('; ') : '—';
  }
  if (Array.isArray(v)) {
    const kept = v.filter((x) => x !== '<UNKNOWN>');
    return kept.length ? kept.join(', ') : '—';
  }
  return v === '<UNKNOWN>' ? '—' : String(v);
}

const FieldEvidenceView: React.FC<FieldEvidenceViewProps> = ({ fieldNames, onEdit, editDisabled = false }) => {
  const [viewMode, setViewMode] = useState<ViewMode>('overview');
  const [isChatMode, setIsChatMode] = useState(false);
  const [scrollToQuote, setScrollToQuote] = useState<string | null>(null);
  const [scrollTarget, setScrollTarget] = useState<'transcript' | 'notes' | null>(null);

  const fieldsConfig = useAppSelector((state) => state.config.fieldsConfig);
  const transcripts = useAppSelector((state) => state.transcript.transcripts);
  const segments = useAppSelector((state) => state.transcript.segments);
  const currentSession = useAppSelector((state) => state.session.currentSession);
  const notes = useAppSelector((state) => state.session.notes);

  const fieldsKey = fieldNames.join(',');  // stable dep for the group

  const showChatModeToggle = useMemo(() => {
    const allCompleted = transcripts.length > 0 && transcripts.every(t => t.status === 'completed' || t.status === 'postprocess_failed');
    if (!allCompleted) return false;
    return segments.some(s => !s.isPartial && s.speaker && s.speaker !== 'Unknown');
  }, [transcripts, segments]);

  // Union of transcript evidence across every field in the group (deduplicated).
  const transcriptQuotes = useMemo(() => {
    const quotes = new Set<string>();
    for (const t of transcripts) {
      if (!t.evidence) continue;
      for (const f of fieldNames) (t.evidence[f] || []).forEach((q) => quotes.add(q));
    }
    return Array.from(quotes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcripts, fieldsKey]);

  // Union of notes evidence across the group.
  const notesQuotes = useMemo(() => {
    const quotes = new Set<string>();
    for (const f of fieldNames) (currentSession?.notes_evidence?.[f] || []).forEach((q) => quotes.add(q));
    return Array.from(quotes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSession, fieldsKey]);

  // Build full transcript text
  const fullTranscriptText = useMemo(() => {
    return currentSession?.full_text || '';
  }, [currentSession]);

  // Build full notes text
  const fullNotesText = useMemo(() => {
    if (!notes || notes.length === 0) return '';
    return notes.map(n => `[${n.created_by} - ${formatISOTimestampToDateTime(n.created_at)}]\n${n.content}`).join('\n\n');
  }, [notes]);

  const hasNotes = fullNotesText.trim().length > 0;
  const totalEvidence = transcriptQuotes.length + notesQuotes.length;

  const isGroup = fieldNames.length > 1;
  const title = fieldNames.map((f) => getFieldLabel(fieldsConfig, f)).join(' & ');
  const primaryQuestion = getFieldQuestion(fieldsConfig, fieldNames[0]) || getFieldLabel(fieldsConfig, fieldNames[0]);
  const extractedFields = (currentSession?.extracted_fields as Record<string, FieldValue> | undefined) || {};
  const overrideFields = (currentSession?.human_overrides_fields as Record<string, FieldValue> | undefined) || {};
  const anyExtracted = fieldNames.some((f) => !isFieldPending(extractedFields[f]));

  const handleQuoteClick = (quote: string, source: 'transcript' | 'notes') => {
    setViewMode('fulltext');
    setScrollTarget(source);
    setScrollToQuote(quote);
  };

  const handleScrollComplete = () => {
    setScrollToQuote(null);
    setScrollTarget(null);
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ padding: '16px 24px', borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
            <Text type="h400">{title}</Text>
            {onEdit && (
              <Button type="primary" onClick={onEdit} disabled={editDisabled}>
                Edit
              </Button>
            )}
          </div>
          <div style={{ fontSize: FONT_SIZE_BODY, color: TEXT_COLOR_PRIMARY, lineHeight: LINE_HEIGHT_BODY }}>
            <strong>Question:</strong> {primaryQuestion}
          </div>
          {/* Per-field blocks laid out horizontally; wrap to the next row when they don't fit. */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 32px' }}>
            {fieldNames.map((f) => {
              const llm = extractedFields[f];
              const override = overrideFields[f];
              const fieldExtracted = !isFieldPending(llm);
              return (
                <div key={f} style={{ fontSize: FONT_SIZE_BODY, color: TEXT_COLOR_PRIMARY, lineHeight: LINE_HEIGHT_BODY }}>
                  {isGroup && (
                    <div style={{ fontSize: FONT_SIZE_SMALL, fontWeight: 600, color: TEXT_COLOR_PRIMARY, marginBottom: '2px' }}>
                      {getFieldLabel(fieldsConfig, f)}
                    </div>
                  )}
                  <div><strong>LLM Answer:</strong> {formatValue(llm)}</div>
                  {override !== undefined && !fieldValuesEqual(override, llm) && (
                    <div><strong>{fieldExtracted ? 'User Override:' : 'User Select:'}</strong> {formatValue(override)}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
          {anyExtracted && (
            <span style={{
              fontSize: '11px',
              backgroundColor: '#e8f5e9',
              color: '#1b5e20',
              padding: '2px 8px',
              borderRadius: '10px',
              fontWeight: 600,
            }}>
              LLM Extracted
            </span>
          )}
          {totalEvidence > 0 && (
            <span style={{
              fontSize: '11px',
              backgroundColor: EVIDENCE_HIGHLIGHT_BG,
              color: EVIDENCE_HIGHLIGHT_TEXT,
              padding: '2px 8px',
              borderRadius: '10px',
              fontWeight: 600,
            }}>
              {totalEvidence} evidence {totalEvidence === 1 ? 'match' : 'matches'}
            </span>
          )}
        </div>
      </div>

      {/* View Toggle */}
      <div style={{ padding: '8px 24px 0', flexShrink: 0 }}>
        <TabGroup
          value={`tab-${viewMode}`}
          onChange={(v) => setViewMode(v.replace('tab-', '') as ViewMode)}
          type="classic"
        >
          <Tab value="tab-overview">Overview</Tab>
          <Tab value="tab-fulltext">Full Text</Tab>
        </TabGroup>
      </div>

      {/* Content Area */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '16px 24px' }}>
        {viewMode === 'overview' ? renderOverview() : renderFullText()}
      </div>
    </div>
  );

  // ===== Overview View =====
  function renderOverview() {
    if (transcriptQuotes.length === 0 && notesQuotes.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '40px', color: TEXT_COLOR_SECONDARY, fontSize: FONT_SIZE_BODY }}>
          No evidence found for this field.
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', gap: '24px' }}>
        {/* Transcript Evidence Column */}
        <div style={{ flex: hasNotes ? 1 : undefined, width: hasNotes ? undefined : '100%' }}>
          <div style={{ fontWeight: 600, fontSize: FONT_SIZE_BODY, marginBottom: '12px', color: TEXT_COLOR_PRIMARY }}>
            Transcript Evidence ({transcriptQuotes.length})
          </div>
          {transcriptQuotes.length > 0 ? (
            transcriptQuotes.map((quote, idx) => (
              <QuoteContextCard
                key={idx}
                quote={quote}
                sourceText={fullTranscriptText}
                onClick={() => handleQuoteClick(quote, 'transcript')}
              />
            ))
          ) : (
            <div style={{ color: TEXT_COLOR_SECONDARY, fontSize: FONT_SIZE_SMALL, fontStyle: 'italic' }}>
              No transcript evidence
            </div>
          )}
        </div>

        {/* Notes Evidence Column */}
        {hasNotes && (
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: FONT_SIZE_BODY, marginBottom: '12px', color: TEXT_COLOR_PRIMARY }}>
              Notes Evidence ({notesQuotes.length})
            </div>
            {notesQuotes.length > 0 ? (
              notesQuotes.map((quote, idx) => (
                <QuoteContextCard
                  key={idx}
                  quote={quote}
                  sourceText={fullNotesText}
                  onClick={() => handleQuoteClick(quote, 'notes')}
                />
              ))
            ) : (
              <div style={{ color: TEXT_COLOR_SECONDARY, fontSize: FONT_SIZE_SMALL, fontStyle: 'italic' }}>
                No notes evidence
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // ===== Full Text View =====
  function renderFullText() {
    return (
      <div style={{ display: 'flex', gap: '24px', height: '100%' }}>
        {/* Transcript Column */}
        <div style={{
          flex: hasNotes ? 1 : undefined,
          width: hasNotes ? undefined : '100%',
          overflow: 'auto',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <span style={{ fontWeight: 600, fontSize: FONT_SIZE_BODY, color: TEXT_COLOR_PRIMARY }}>
              Transcript
            </span>
            {showChatModeToggle && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Toggle checked={isChatMode} onChange={setIsChatMode} size="small" />
                <span style={{ fontSize: FONT_SIZE_SMALL, color: TEXT_COLOR_SECONDARY }}>Chat</span>
              </div>
            )}
          </div>
          <div style={{ flex: 1, overflow: 'auto' }}>
            {fullTranscriptText ? (
              isChatMode ? (
                <TranscriptChatView quotes={transcriptQuotes} scrollToQuote={scrollTarget === 'transcript' ? scrollToQuote : null} onScrollComplete={handleScrollComplete} />
              ) : (
                <EvidenceHighlightedText
                  text={fullTranscriptText}
                  quotes={transcriptQuotes}
                  scrollToQuote={scrollTarget === 'transcript' ? scrollToQuote : null}
                  onScrollComplete={handleScrollComplete}
                />
              )
            ) : (
              <div style={{ color: TEXT_COLOR_SECONDARY, fontStyle: 'italic', fontSize: FONT_SIZE_BODY }}>No transcript available</div>
            )}
          </div>
        </div>

        {/* Notes Column */}
        {hasNotes && (
          <div style={{
            flex: 1,
            overflow: 'auto',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            padding: '16px',
          }}>
            <div style={{ fontWeight: 600, fontSize: FONT_SIZE_BODY, marginBottom: '12px', color: TEXT_COLOR_PRIMARY }}>
              Notes
            </div>
            <EvidenceHighlightedText
              text={fullNotesText}
              quotes={notesQuotes}
              scrollToQuote={scrollTarget === 'notes' ? scrollToQuote : null}
              onScrollComplete={handleScrollComplete}
            />
          </div>
        )}
      </div>
    );
  }
};

// ===== Quote Context Card =====
// Shows: ...{grey context}{highlighted evidence}{grey context}...

const QuoteContextCard: React.FC<{ quote: string; sourceText: string; onClick: () => void }> = ({ quote, sourceText, onClick }) => {
  // Find quote position in source and extract context
  const idx = sourceText.toLowerCase().indexOf(quote.toLowerCase());

  let before = '';
  let after = '';

  if (idx >= 0) {
    const start = Math.max(0, idx - CONTEXT_CHARS);
    const end = Math.min(sourceText.length, idx + quote.length + CONTEXT_CHARS);
    before = (start > 0 ? '...' : '') + sourceText.slice(start, idx);
    after = sourceText.slice(idx + quote.length, end) + (end < sourceText.length ? '...' : '');
  }

  return (
    <div
      onClick={onClick}
      style={{
        padding: '10px 12px',
        marginBottom: '8px',
        borderLeft: `3px solid ${EVIDENCE_HIGHLIGHT_BORDER}`,
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: FONT_SIZE_BODY,
        lineHeight: LINE_HEIGHT_BODY,
        backgroundColor: '#fafafa',
        transition: 'background-color 0.2s',
      }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#f0f0f0'; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#fafafa'; }}
    >
      <span style={{ color: TEXT_COLOR_SECONDARY }}>{before}</span>
      <span style={{ backgroundColor: EVIDENCE_HIGHLIGHT_BG, color: TEXT_COLOR_PRIMARY, fontWeight: 500, padding: '1px 2px', borderRadius: '2px' }}>
        {idx >= 0 ? sourceText.slice(idx, idx + quote.length) : quote}
      </span>
      <span style={{ color: TEXT_COLOR_SECONDARY }}>{after}</span>
      <span style={{ color: TEXT_COLOR_LINK, fontSize: FONT_SIZE_SMALL, marginLeft: '8px' }}>
        View →
      </span>
    </div>
  );
};

// ===== Transcript Chat View (speaker segments with highlights) =====

const TranscriptChatView: React.FC<{
  quotes: string[];
  scrollToQuote: string | null;
  onScrollComplete: () => void;
}> = ({ quotes, scrollToQuote, onScrollComplete }) => {
  const mergedSegments = useAppSelector(selectMergedSegments);

  const getSpeakerColor = (speaker: string | null): string => {
    if (!speaker || speaker === 'Unknown') return TEXT_COLOR_SECONDARY;
    const colors: Record<string, string> = {
      'Speaker A': '#1565c0',
      'Speaker B': '#c62828',
      'Speaker C': '#2e7d32',
      'Speaker D': '#6a1b9a',
    };
    return colors[speaker] || TEXT_COLOR_SECONDARY;
  };

  if (mergedSegments.length === 0) {
    return <div style={{ color: TEXT_COLOR_SECONDARY, fontStyle: 'italic', fontSize: FONT_SIZE_BODY }}>No segments available</div>;
  }

  return (
    <div>
      {mergedSegments.map((segment, idx) => (
        <div key={idx} style={{ marginBottom: '8px', lineHeight: LINE_HEIGHT_BODY, fontSize: FONT_SIZE_BODY }}>
          <span style={{ fontWeight: 'bold', color: getSpeakerColor(segment.speaker), fontSize: FONT_SIZE_SMALL }}>
            [{segment.speaker || 'Unknown'}]
          </span>{' '}
          <EvidenceHighlightedText
            text={segment.text}
            quotes={quotes}
            scrollToQuote={scrollToQuote}
            onScrollComplete={onScrollComplete}
          />
        </div>
      ))}
    </div>
  );
};

export default FieldEvidenceView;
